<script setup>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ChatMessagesView from '@/components/chat/ChatMessagesView.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import PromptManager from '@/components/chat/prompts/PromptManager.vue'
import ReportsManager from '@/components/chat/reports/ReportsManager.vue'
import { formatTime } from '@/utils/datetime'

const props = defineProps({
  messages: {
    type: Array,
    default: () => []
  },
  isGenerating: {
    type: Boolean,
    default: false
  },
  session: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['send-message'])
const store = useStore()
const router = useRouter()

const currentModel = ref('')

const handleSendMessage = (content) => {
  emit('send-message', content)
}

// Load current model from session
watch(() => props.session, (session) => {
  if (session?.llm_config?.model_name) {
    currentModel.value = session.llm_config.model_name
  }
}, { immediate: true })

// Create new session when model changes (like Anthropic's chat)
const handleModelChange = async (newModel) => {
  if (!props.session || currentModel.value === newModel) return

  const userTz = store.state.user.tz || 'UTC'
  const defaultName = formatTime(new Date(), userTz, 'MMMM DD, YYYY')

  try {
    // Create new session with selected model
    const newSession = await store.dispatch('chat/createSession', {
      orgId: props.session.organization_id,
      name: defaultName,
      llmConfig: { model_name: newModel }
    })

    // Navigate to new session
    router.push({
      name: 'ChatSession',
      params: {
        org_id: props.session.organization_id,
        session_code: newSession.session_code
      }
    })
  } catch (error) {
    console.error('Failed to create new session:', error)
    // Revert model selector to current model on error
    currentModel.value = props.session.llm_config.model_name
  }
}
</script>

<template>
  <div class="flex flex-col bg-background h-full border-r border-border overflow-hidden">
    <!-- Messages View -->
    <div class="flex-1 overflow-y-auto min-h-0">
      <ChatMessagesView
        :messages="messages"
        :is-generating="isGenerating"
      />
    </div>

    <!-- Prompt Management Panel (Connections) -->
    <PromptManager
      v-if="session?.session_uuid && session?.organization_id"
      :session-uuid="session.session_uuid"
      :organization-id="session.organization_id"
      class="flex-shrink-0"
    />

    <!-- Reports Panel -->
    <ReportsManager
      v-if="session?.session_uuid && session?.organization_id"
      :session-uuid="session.session_uuid"
      :organization-id="session.organization_id"
      :is-generating="isGenerating"
      class="flex-shrink-0"
    />

    <!-- Input Bar -->
    <div class="border-t border-border p-3 flex-shrink-0">
      <ChatInput
        placeholder="Type a message..."
        :disabled="isGenerating"
        :model-value="currentModel"
        :org-id="session?.organization_id"
        @update:model-value="handleModelChange"
        @send="handleSendMessage"
      />
    </div>
  </div>
</template>
